import { Injectable, inject } from '@angular/core';

import { AuthService, AutoLoginParameters, DslService, LoginNavigationService, NavigationService, UserService } from '@frontend/vanilla/core';
import { first, firstValueFrom } from 'rxjs';

import { LoginService } from '../login.service';
import { DanskeSpilLoginService } from './danske-spil-login.service';
import { LoginIntegrationConfig, LoginIntegrationType } from './login-integration.client-config';

const LOGIN_SUCCESS_PAGE_PLACEHOLDER: string = '{LOGIN_SUCCESS_PAGE}';

@Injectable({ providedIn: 'root' })
export class LoginIntegrationService {
    private readonly navigation = inject(NavigationService);
    private readonly config = inject(LoginIntegrationConfig);
    private readonly danskeSpilLoginService = inject(DanskeSpilLoginService);
    private readonly dslService = inject(DslService);
    private readonly user = inject(UserService);
    private readonly authService = inject(AuthService);
    private readonly loginService = inject(LoginService);
    private readonly loginNavigationService = inject(LoginNavigationService);

    get redirectEnabled(): boolean {
        return (
            this.config.type === LoginIntegrationType.NinjaCasinoSe ||
            (this.config.type === LoginIntegrationType.DanskeSpilDk && this.config.options.version === 2)
        );
    }

    redirectToLogin(): Promise<any> {
        this.loginNavigationService.storeReturnUrl();

        const standaloneLoginUrl = this.config.options.standaloneLoginUrl.toString();
        const url =
            standaloneLoginUrl.indexOf(LOGIN_SUCCESS_PAGE_PLACEHOLDER) !== -1
                ? standaloneLoginUrl.replace(
                      LOGIN_SUCCESS_PAGE_PLACEHOLDER,
                      this.navigation.location.search.get('rurl') ?? this.navigation.location.absUrl(),
                  )
                : this.config.options.standaloneLoginUrl;

        return this.navigation.goTo(url);
    }

    async init() {
        await firstValueFrom(this.config.whenReady);

        if (this.config.type === LoginIntegrationType.DanskeSpilDk) {
            this.danskeSpilLoginService.isSessionActive().subscribe((sessionActive: boolean) => {
                if (sessionActive && !this.user.isAuthenticated) {
                    this.danskeSpilLoginService.getLoginParameters().subscribe((loginParameters: AutoLoginParameters) => {
                        this.dslService
                            .evaluateExpression<boolean>(this.config.options.redirectAfterLogin)
                            .pipe(first())
                            .subscribe(async (redirectAfterLogin: boolean) => {
                                await this.loginService.autoLogin(loginParameters, redirectAfterLogin);
                            });
                    });
                } else if (!sessionActive && this.user.isAuthenticated) {
                    this.authService.logout();
                }
            });
        }
    }

    async logout(): Promise<boolean> {
        await firstValueFrom(this.config.whenReady);

        if (this.config.type === LoginIntegrationType.DanskeSpilDk) {
            return this.danskeSpilLoginService.logout();
        }

        return Promise.resolve(true);
    }
}
